/**
 * Create viewer from StorySplat Scene ID
 *
 * This module provides functionality to create a viewer by fetching
 * scene data from the StorySplat API using a scene ID.
 */

import { createViewer } from './createViewer';
import { PROD_ASSET_RENEWAL_ENDPOINT } from './AssetLeaseManager';
import { getOrCreateVisitorId, safeRandomUUID } from './analyticsConsent';
import { showLegacyViewerBanner } from './viewerUI';
import type { ViewerInstance, ViewerOptions, SceneData, ViewerLoadedData } from '../types';

/**
 * Track embedded scene usage (views and bandwidth)
 * Sends tracking data to StorySplat API for analytics
 */
async function trackEmbedUsage(
  baseUrl: string,
  sceneId: string,
  ownerId: string,
  type: 'view' | 'bandwidth',
  bytes?: number,
  visitorId?: string | null,
  bandwidthReport?: { reportId: string; reportSequence: number },
): Promise<void> {
  try {
    const response = await fetch(`${baseUrl}/api/track-embed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sceneId,
        ownerId,
        type,
        ...(visitorId ? { visitorId } : {}),
        ...(type === 'bandwidth' && bytes ? { bytes } : {}),
        ...(type === 'bandwidth' && bandwidthReport ? bandwidthReport : {}),
      })
    });

    if (!response.ok) {
      console.warn(`[StorySplat] Failed to track ${type}:`, response.status);
    } else {
      console.log(`[StorySplat] Tracked ${type}${type === 'bandwidth' ? ` (${(bytes! / 1024 / 1024).toFixed(2)}MB)` : ''}`);
    }
  } catch (error) {
    // Silently fail - tracking shouldn't break the viewer
    console.warn(`[StorySplat] Error tracking ${type}:`, error);
  }
}

/**
 * Options for createViewerFromSceneId
 */
export interface ViewerFromSceneIdOptions extends ViewerOptions {
  /**
   * Base URL for the StorySplat API
   * @default 'https://discover.storysplat.com'
   */
  baseUrl?: string;

  /**
   * API key for accessing private scenes (future feature)
   */
  apiKey?: string;
}

/**
 * API response format from /api/scene/{sceneId}
 */
interface SceneApiResponse {
  success: boolean;
  data: SceneData;
  meta: {
    sceneId: string;
    ownerId: string;    // User ID for tracking
    name: string;
    description?: string;
    userName?: string;  // May be undefined for anonymous/deleted users
    userSlug?: string;  // May be undefined for anonymous/deleted users
    thumbnailUrl: string;
    splatUrl: string;
    htmlUrl: string;
    playcanvasHtmlUrl?: string;
    views: number;
    createdAt: string | null;
    assetLeaseExpiresAtMs?: number;
  };
}

/**
 * Error thrown when scene fetching fails
 */
export class SceneNotFoundError extends Error {
  constructor(sceneId: string) {
    super(`Scene not found: ${sceneId}`);
    this.name = 'SceneNotFoundError';
  }
}

/**
 * Error thrown when API request fails
 */
export class SceneApiError extends Error {
  public statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'SceneApiError';
    this.statusCode = statusCode;
  }
}

/**
 * Get the default API base URL from environment or fallback to production
 */
function getDefaultBaseUrl(): string {
  // Check for environment variable (works in Node.js, bundlers with define, etc.)
  if (typeof process !== 'undefined' && process.env?.STORYSPLAT_API_URL) {
    return process.env.STORYSPLAT_API_URL;
  }
  // Check for window-based config (for browser environments)
  const browserWindow = typeof window !== 'undefined'
    ? (window as Window & { __STORYSPLAT_API_URL__?: string })
    : undefined;
  if (browserWindow?.__STORYSPLAT_API_URL__) {
    return browserWindow.__STORYSPLAT_API_URL__;
  }
  // Default to production
  return 'https://discover.storysplat.com';
}

/**
 * Default API base URL
 */
const DEFAULT_BASE_URL = getDefaultBaseUrl();

/**
 * Create a viewer from a StorySplat scene ID
 *
 * This function fetches scene data from the StorySplat API and creates
 * an embedded viewer. The scene must be public and owned by a user
 * with a public profile.
 *
 * @param container - HTML element to render the viewer in
 * @param sceneId - StorySplat scene ID from your dashboard
 * @param options - Viewer options including API configuration
 * @returns Promise resolving to ViewerInstance
 *
 * @example
 * ```typescript
 * import { createViewerFromSceneId } from 'storysplat-viewer';
 *
 * const viewer = await createViewerFromSceneId(
 *   document.getElementById('viewer')!,
 *   'YOUR_SCENE_ID'
 * );
 *
 * // Control playback
 * viewer.play();
 * viewer.pause();
 *
 * // Navigate
 * viewer.nextWaypoint();
 * viewer.goToWaypoint(2);
 *
 * // Listen for events
 * viewer.on('ready', () => console.log('Viewer ready!'));
 * ```
 */
export async function createViewerFromSceneId(
  container: HTMLElement,
  sceneId: string,
  options: ViewerFromSceneIdOptions = {}
): Promise<ViewerInstance> {
  const baseUrl = options.baseUrl || DEFAULT_BASE_URL;

  console.log(`[StorySplat Viewer] Fetching scene: ${sceneId}`);

  // Build request URL
  const apiUrl = `${baseUrl}/api/scene/${encodeURIComponent(sceneId)}`;

  // Build headers
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add API key if provided (for future private scene support)
  if (options.apiKey) {
    headers['Authorization'] = `Bearer ${options.apiKey}`;
  }

  // Fetch scene data
  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new SceneNotFoundError(sceneId);
    }

    const errorText = await response.text();
    let errorMessage: string;

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error || `API error: ${response.status}`;
    } catch {
      errorMessage = `API error: ${response.status}`;
    }

    throw new SceneApiError(errorMessage, response.status);
  }

  const apiResponse: SceneApiResponse = await response.json();

  if (!apiResponse.success || !apiResponse.data) {
    throw new SceneApiError('Invalid API response format', 500);
  }

  console.log(`[StorySplat Viewer] Scene loaded: "${apiResponse.meta.name}"`);

  // Extract tracking info
  const ownerId = apiResponse.meta.ownerId;

  // Merge scene metadata into scene data if not present
  const sceneData: SceneData = {
    ...apiResponse.data,
    // Ensure these are set from metadata if not in scene data
    name: apiResponse.data.name || apiResponse.meta.name,
    thumbnailUrl: apiResponse.data.thumbnailUrl || apiResponse.meta.thumbnailUrl,
  };

  // Extract viewer-specific options (remove API-specific ones)
  const { baseUrl: _baseUrl, apiKey: _apiKey, ...viewerOptions } = options;

  // Create the viewer — enable analytics unless caller explicitly opted out.
  // Opt-out: pass `analytics: undefined` or `analytics: false` in options,
  // or set `disableAnalytics: true` for consent/privacy flows.
  // This is used by SessionReplayPlayer to prevent replays from counting as new views.
  const analyticsDisabled = viewerOptions.disableAnalytics === true;
  const analyticsEnabled = !('analytics' in options) && !analyticsDisabled;
  // Get legacy HTML URL for fallback if splat loading fails
  const legacyHtmlUrl = apiResponse.meta.htmlUrl || apiResponse.meta.playcanvasHtmlUrl || null;

  const viewer = await createViewer(container, sceneData, {
    ...viewerOptions,
    ...(analyticsEnabled && { analytics: { sceneId, ownerId, baseUrl } }),
    ...(Number.isSafeInteger(apiResponse.meta.assetLeaseExpiresAtMs) ? {
      assetLease: {
        sceneId,
        ownerId,
        expiresAtMs: apiResponse.meta.assetLeaseExpiresAtMs as number,
        renewalEndpoint: (() => {
          try {
            if (typeof window === 'undefined') return PROD_ASSET_RENEWAL_ENDPOINT;
            const apiOrigin = new URL(baseUrl, window.location.href).origin;
            return apiOrigin === window.location.origin
              ? '/api/asset-access/renew'
              : PROD_ASSET_RENEWAL_ENDPOINT;
          } catch {
            return PROD_ASSET_RENEWAL_ENDPOINT;
          }
        })(),
      },
    } : {}),
  });

  // If splat fails to load and a legacy HTML export exists, fall back to iframe
  if (legacyHtmlUrl) {
    viewer.on('error', (err: Error) => {
      if (err?.message?.includes('Failed to load splat from any URL')) {
        console.log('[StorySplat] Falling back to legacy viewer:', legacyHtmlUrl);
        // Destroy the PlayCanvas viewer to release WebGL context and RAF loop
        try { viewer.destroy(); } catch (e) { /* ignore */ }
        // Replace container contents with an iframe loading the legacy export
        const iframe = document.createElement('iframe');
        iframe.src = legacyHtmlUrl;
        iframe.style.cssText = 'width:100%;height:100%;border:none;position:absolute;top:0;left:0;';
        iframe.allow = 'fullscreen; autoplay';
        iframe.title = apiResponse.meta.name || 'StorySplat Scene';
        // Clear the container (including the viewer's load-failure notice) and insert the iframe
        container.replaceChildren();
        container.style.position = 'relative';
        container.appendChild(iframe);
        showLegacyViewerBanner(container);
      }
    });
  }

  if (analyticsEnabled) {
    // The same visitor id the SessionRecorder uses. Without it the server had
    // nothing to dedup on, so every load / refresh / back-navigation counted as
    // a brand-new view. `null` means the visitor opted out (DNT, GPC,
    // ?analytics=off, opt-out key) — in that case send no ping at all.
    const visitorId = getOrCreateVisitorId();

    if (visitorId) {
      // Track view (fire and forget)
      trackEmbedUsage(baseUrl, sceneId, ownerId, 'view', undefined, visitorId);
    }

    // Track bandwidth when scene loads (only for StorySplat-hosted files).
    // Newer runtimes meter streaming bandwidth themselves and post their own
    // reports (initial + periodic deltas) — they signal that with
    // bandwidthSelfReported so this legacy one-shot must not double-count.
    let bandwidthTracked = false;
    viewer.on('loaded', (data?: ViewerLoadedData) => {
      if (bandwidthTracked) return; // Only track once
      bandwidthTracked = true;

      if (data?.bandwidthSelfReported) {
        console.log('[StorySplat] Bandwidth reported by viewer runtime (streaming meter)');
      } else if (data && data.bandwidthUsed > 0 && data.isStorySplatHosted) {
        trackEmbedUsage(
          baseUrl,
          sceneId,
          ownerId,
          'bandwidth',
          data.bandwidthUsed,
          undefined,
          { reportId: safeRandomUUID(), reportSequence: 0 },
        );
      } else {
        console.log('[StorySplat] Bandwidth not tracked (self-hosted or no data)');
      }
    });
  }

  return viewer;
}

/**
 * Fetch scene metadata without creating a viewer
 *
 * Useful for displaying scene information before loading the full viewer.
 *
 * @param sceneId - StorySplat scene ID
 * @param options - API configuration options
 * @returns Promise resolving to scene metadata
 *
 * @example
 * ```typescript
 * import { fetchSceneMeta } from 'storysplat-viewer';
 *
 * const meta = await fetchSceneMeta('YOUR_SCENE_ID');
 * console.log(`Scene: ${meta.name} by ${meta.userName}`);
 * console.log(`Views: ${meta.views}`);
 * ```
 */
export async function fetchSceneMeta(
  sceneId: string,
  options: { baseUrl?: string; apiKey?: string } = {}
): Promise<{
  name: string;
  description: string;
  thumbnailUrl: string;
  userName: string;   // Defaults to 'Unknown' if not available
  userSlug: string;   // Defaults to 'unknown' if not available
  views: number;
  tags: string[];
  category?: string;
  createdAt: string | null;
}> {
  const baseUrl = options.baseUrl || DEFAULT_BASE_URL;
  const apiUrl = `${baseUrl}/api/scene/${encodeURIComponent(sceneId)}/meta`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (options.apiKey) {
    headers['Authorization'] = `Bearer ${options.apiKey}`;
  }

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new SceneNotFoundError(sceneId);
    }
    throw new SceneApiError(`API error: ${response.status}`, response.status);
  }

  const data = await response.json();

  // Ensure userName and userSlug have fallback values
  return {
    ...data,
    userName: data.userName || 'Unknown',
    userSlug: data.userSlug || 'unknown',
  };
}
